import { activeLanguage } from '../i18n/language';
import { noticeBody, noticeParagraphs, type BilingualText } from '../lib/privacyNotice';
import { colors } from '../styles/theme';

interface Props {
  /** The document in force, once it has been read. */
  readonly notice: (BilingualText & { is_draft: boolean }) | null;
  /** True when it could not be read. */
  readonly unavailable: boolean;
  /** Which document this is; the privacy notice unless it says otherwise. */
  readonly document?: 'privacy' | 'terms';
}

/**
 * A legal document as the person reads it: a DRAFT banner while the text is a placeholder, its
 * paragraphs in their language, and a plain line when it cannot be read. Shared by the public
 * pages and the page that asks a client to accept a new version (the privacy notice, #289, and
 * the Terms and Conditions, 0181).
 */
export default function NoticeBody({
  notice,
  unavailable,
  document = 'privacy',
}: Readonly<Props>): JSX.Element {
  const paragraphs = notice ? noticeParagraphs(noticeBody(notice, activeLanguage())) : [];
  const terms = document === 'terms';
  return (
    <>
      {notice?.is_draft && (
        <p
          role="note"
          className="mb-4 rounded-lg p-3 text-sm font-semibold"
          style={{
            background: colors.warningBg,
            color: colors.warning,
            border: `1px solid ${colors.warning}`,
          }}
        >
          {terms
            ? 'DRAFT: this is a placeholder text, not the final Terms and Conditions.'
            : 'DRAFT: this is a placeholder text, not the final privacy notice.'}
        </p>
      )}
      {unavailable && (
        <p role="alert" className="text-sm" style={{ color: colors.danger }}>
          {terms
            ? 'The Terms and Conditions are not available right now. Please try again later.'
            : 'The privacy notice is not available right now. Please try again later.'}
        </p>
      )}
      {!notice && !unavailable && (
        <p className="text-sm" style={{ color: colors.muted }}>
          Loading…
        </p>
      )}
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="mb-3 text-sm leading-relaxed" style={{ color: colors.inkSoft }}>
          {paragraph}
        </p>
      ))}
    </>
  );
}
